import { useCloudSettingsQuery, useGridFilters } from "@repo/baseCloud";
import { SegmentFilters } from "@repo/baseCloud/components";
import { enumToOptions } from "@repo/helpers";
import { DateTimePicker, DropDownList, FiltersV2, Loading, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";

import type { ComplaintsFilter } from "../../models";

import ComplaintsApi from "../../api/complaintsApi";
import { QUERY_KEYS } from "../../api/queries";
import { yesNoOptions } from "../../constants/constants";
import { CommodityTypeEnum, RepIncidentAssignedStatus, RepIncidentStatus } from "../../models";
import { CustomerIncidentsGrid } from "./grid";
import {
	AGENT_ID,
	ASSIGNED_STATUS,
	COMMISSION_COMPLAINT,
	COMMODITY,
	COMPLAINT_DATE_AFTER,
	COMPLAINT_DATE_BEFORE,
	COMPLAINT_ID,
	COMPLAINT_OR_INCIDENT,
	CREATED_ON_FROM,
	CREATED_ON_TO,
	CUSTOMER_ACCOUNT_NUMBER,
	CUSTOMER_NAME,
	CUSTOMER_PHONE,
	ENROLLMENT_DATE_AFTER,
	ENROLLMENT_DATE_BEFORE,
	ESI_ID,
	OPE,
	AFFILIATE_PARTNER_NAME,
	QA_ANALYST_ID,
	REPORTING_TAG_ID,
	REPRESENTATIVE_FIRST_NAME,
	REPRESENTATIVE_LAST_NAME,
	SOURCE_ID,
	STATUS,
	TYPE_ID,
} from "./strings";

const initialFilters: ComplaintsFilter = {
	currentPage: 1,
	itemsPerPage: 10,
	complaintDateAfter: null,
	complaintDateBefore: null,
	createdOnFrom: null,
	createdOnTo: null,
	enrollmentDateAfter: null,
	enrollmentDateBefore: null,
	customerAccountNumber: "",
	repId: "",
	firstName: "",
	lastName: "",
	representativeFirstName: "",
	representativeLastName: "",
	esiId: "",
	agentId: "",
	customerName: "",
	customerPhone: "",
	complaintId: "",
	status: "",
	assignedStatus: "",
	qaAnalystId: null,
	sourceId: null,
	reportingTagId: null,
	affiliatePartnerName: null,
	complaintOrIncident: null,
	commissionComplaint: null,
	commodity: "",
	ope: null,
	typeId: [],
	branchIds: [],
};

export function CustomerIncidentsPage() {
	const {
		filters: draftFilters,
		setFilters: setDraftFilters,
		appliedFilters,
		setAppliedFilters,
		filtersProps,
	} = useGridFilters(initialFilters);

	// Data queries for dropdown options
	const complaintTypesQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ComplaintTypes],
		queryFn: () => ComplaintsApi.getComplaintTypes(),
	});

	const complaintSourcesQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ComplaintSources],
		queryFn: () => ComplaintsApi.getComplaintSources(),
	});

	const usersQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.Users],
		queryFn: () => ComplaintsApi.getUsers(),
	});

	const reportingTagsQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ReportingTags, draftFilters.branchIds],
		queryFn: () => ComplaintsApi.getReportingTags(draftFilters.branchIds),
	});

	const affiliatePartnersQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.AffiliatePartners],
		queryFn: () => ComplaintsApi.getAffiliatePartners(),
	});
	//Partner options are the selected brands' partners; the filter matches by name, so each name is listed once
	const selectedBrands = draftFilters.brands ?? [];
	const affiliatePartnerOptions = [
		...new Set(
			(affiliatePartnersQuery.data ?? []).filter((p) => selectedBrands.includes(p.BrandId)).map((p) => p.Name),
		),
	].map((name) => ({ Id: name, Name: name }));

	const complaintOrIncidentQuery = useQuery({
		queryKey: [QUERY_KEYS.Complaints.ComplaintOrIncident],
		queryFn: () => ComplaintsApi.getComplaintOrIncident(),
	});

	const { settings, settingsLoading } = useCloudSettingsQuery();

	const filterChanged = (value: any, name: string) => {
		setDraftFilters((prev) => ({ ...prev, [name]: value }));
	};

	const segmentFiltersChanged = useCallback(
		(s) =>
			setDraftFilters((prev) => ({
				...prev,
				//The partner options depend on the brands, so a partner picked earlier may no longer apply
				affiliatePartnerName:
					String(prev.brands ?? []) === String(s.brandIds ?? []) ? prev.affiliatePartnerName : null,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setDraftFilters],
	);

	return (
		// The grid keeps a column's first visibility, so it waits for the ZPV and Delete settings before mounting.
		<Loading loading={settingsLoading}>
			<main>
				<FiltersV2 {...filtersProps}>
					{/* Date Filters */}
					<DateTimePicker
						id={COMPLAINT_DATE_AFTER}
						name={COMPLAINT_DATE_AFTER}
						label="Date of Incident after"
						value={draftFilters.complaintDateAfter}
						changeHandler={filterChanged}
					/>
					<DateTimePicker
						id={COMPLAINT_DATE_BEFORE}
						name={COMPLAINT_DATE_BEFORE}
						label="Date of Incident before"
						value={draftFilters.complaintDateBefore}
						changeHandler={filterChanged}
					/>
					<DateTimePicker
						id={CREATED_ON_FROM}
						name={CREATED_ON_FROM}
						label="Created On From"
						value={draftFilters.createdOnFrom}
						changeHandler={filterChanged}
					/>
					<DateTimePicker
						id={CREATED_ON_TO}
						name={CREATED_ON_TO}
						label="Created On To"
						value={draftFilters.createdOnTo}
						changeHandler={filterChanged}
					/>
					<SegmentFilters onChange={segmentFiltersChanged} />

					<TextBox
						id={REPRESENTATIVE_FIRST_NAME}
						name={REPRESENTATIVE_FIRST_NAME}
						label="Representative First Name"
						value={draftFilters.representativeFirstName}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={REPRESENTATIVE_LAST_NAME}
						name={REPRESENTATIVE_LAST_NAME}
						label="Representative Last Name"
						value={draftFilters.representativeLastName}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={ESI_ID}
						name={ESI_ID}
						label="ESId"
						value={draftFilters.esiId}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={CUSTOMER_ACCOUNT_NUMBER}
						name={CUSTOMER_ACCOUNT_NUMBER}
						label="Customer Account Number"
						value={draftFilters.customerAccountNumber}
						changeHandler={filterChanged}
					/>
					<DropDownList
						id={STATUS}
						name={STATUS}
						label="Status"
						value={draftFilters.status}
						changeHandler={filterChanged}
						fullLength
						clearable
						values={enumToOptions(RepIncidentStatus)}
					/>
					<DropDownList
						id={ASSIGNED_STATUS}
						name={ASSIGNED_STATUS}
						label="Assigned Status"
						value={draftFilters.assignedStatus}
						changeHandler={filterChanged}
						fullLength
						clearable
						values={enumToOptions(RepIncidentAssignedStatus)}
					/>
					<DropDownList
						id={QA_ANALYST_ID}
						name={QA_ANALYST_ID}
						label="QA Analyst"
						value={draftFilters.qaAnalystId}
						changeHandler={filterChanged}
						fullLength
						clearable
						loaded={usersQuery.isFetched}
						values={usersQuery.data ?? []}
						valueProp="Id"
						textProp="Name"
					/>
					<DropDownList
						id={TYPE_ID}
						name={TYPE_ID}
						label="Type"
						value={draftFilters.typeId}
						changeHandler={filterChanged}
						fullLength
						isMulti
						clearable
						loaded={complaintTypesQuery.isFetched}
						values={complaintTypesQuery.data ?? []}
						textProp="Description"
						valueProp="Id"
					/>
					<DropDownList
						id={SOURCE_ID}
						name={SOURCE_ID}
						label="Source"
						value={draftFilters.sourceId}
						changeHandler={filterChanged}
						fullLength
						clearable
						loaded={complaintSourcesQuery.isFetched}
						values={complaintSourcesQuery.data ?? []}
						textProp="Description"
						valueProp="Id"
					/>
					<DropDownList
						id={REPORTING_TAG_ID}
						name={REPORTING_TAG_ID}
						label="Reporting Tag"
						value={draftFilters.reportingTagId}
						changeHandler={filterChanged}
						fullLength
						clearable
						loaded={reportingTagsQuery.isFetched}
						values={reportingTagsQuery.data ?? []}
						textProp="Name"
						valueProp="Id"
					/>
					{affiliatePartnersQuery.data?.length > 0 && (
						<DropDownList
							id={AFFILIATE_PARTNER_NAME}
							name={AFFILIATE_PARTNER_NAME}
							label="Online Affiliate Partner"
							value={draftFilters.affiliatePartnerName}
							changeHandler={filterChanged}
							fullLength
							clearable
							disabled={!affiliatePartnerOptions.length}
							loaded={affiliatePartnersQuery.isFetched}
							values={affiliatePartnerOptions}
							textProp="Name"
							valueProp="Id"
						/>
					)}
					<TextBox
						id={AGENT_ID}
						name={AGENT_ID}
						label="Rep Id"
						value={draftFilters.agentId}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={CUSTOMER_NAME}
						name={CUSTOMER_NAME}
						label="Customer Name"
						value={draftFilters.customerName}
						changeHandler={filterChanged}
					/>
					<DateTimePicker
						id={ENROLLMENT_DATE_AFTER}
						name={ENROLLMENT_DATE_AFTER}
						label="Date of Enrollment after"
						value={draftFilters.enrollmentDateAfter}
						changeHandler={filterChanged}
					/>
					<DateTimePicker
						id={ENROLLMENT_DATE_BEFORE}
						name={ENROLLMENT_DATE_BEFORE}
						label="Date of Enrollment before"
						value={draftFilters.enrollmentDateBefore}
						changeHandler={filterChanged}
					/>
					<TextBox
						id={CUSTOMER_PHONE}
						name={CUSTOMER_PHONE}
						label="Customer Phone"
						value={draftFilters.customerPhone}
						changeHandler={filterChanged}
					/>
					<DropDownList
						id={COMPLAINT_OR_INCIDENT}
						name={COMPLAINT_OR_INCIDENT}
						label="Incident Category"
						value={draftFilters.complaintOrIncident}
						changeHandler={filterChanged}
						fullLength
						clearable
						loaded={complaintOrIncidentQuery.isFetched}
						values={complaintOrIncidentQuery.data ?? []}
						textProp="Name"
						valueProp="Id"
					/>
					{settings?.customerIncidents.showCommissionComplaint && (
						<DropDownList
							id={COMMISSION_COMPLAINT}
							name={COMMISSION_COMPLAINT}
							label="ZPV"
							value={draftFilters.commissionComplaint}
							changeHandler={filterChanged}
							fullLength
							clearable
							values={yesNoOptions}
						/>
					)}
					<DropDownList
						id={COMMODITY}
						name={COMMODITY}
						label="Commodity"
						value={draftFilters.commodity}
						changeHandler={filterChanged}
						fullLength
						clearable
						values={enumToOptions(CommodityTypeEnum)}
					/>
					<DropDownList
						id={OPE}
						name={OPE}
						label="Office of the President/Executive"
						value={draftFilters.ope}
						changeHandler={filterChanged}
						fullLength
						clearable
						values={yesNoOptions}
					/>
					<TextBox
						id={COMPLAINT_ID}
						name={COMPLAINT_ID}
						label="Complaint ID"
						value={draftFilters.complaintId}
						changeHandler={filterChanged}
					/>
				</FiltersV2>
				<CustomerIncidentsGrid filters={appliedFilters} setFilters={setAppliedFilters} />
			</main>
		</Loading>
	);
}
